import {
  ChartsFilter,
  ComparedPeriodType,
  Div,
  DragScrollX,
  FinancesComparedOverviewEntity,
  FinancesOverviewEntity,
  getFinancesComparedOverview,
  getFinancesOverview,
  getTimeZone,
  GroupByType,
  renderDateLabelWithToday,
  reportError,
  useAuth,
  UserRole,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BalanceFinances } from './BalanceFinances';
import { FinancesCharts } from './FinancesCharts';
import { GamesFinances } from './Games/GamesFinances';
import { BrandPayoutList } from './BrandPayoutList';
import { ClientOutstandingBalance } from './ClientOutstandingBalance';
import { IncomeLocationsList } from './IncomeLocationsList';
import { SaasEligibleLocations } from './SaasEligibleLocations';

/**
 * Pick an appropriate bucket size for the chosen date range — mirrors what
 * OrdersSection does. The /compare endpoint does NOT support `year`, so we
 * coerce down to `month` when comparing.
 */
function getGroupByType(fromISO: string, toISO: string): GroupByType {
  const fromDate = DateTime.fromISO(fromISO);
  const toDate = DateTime.fromISO(toISO);
  const diffInDays = toDate.diff(fromDate, 'days').days;
  const diffInMonths = toDate.diff(fromDate, 'months').months;

  if (diffInDays < 1) return GroupByType.HOUR;
  if (diffInDays <= 31) return GroupByType.DAY;
  if (diffInMonths <= 12) return GroupByType.MONTH;
  return GroupByType.YEAR;
}

export const FinancesSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useAuth();
  // Support sees a limited view: only the loss charts, the brand payout
  // lists and the SaaS eligible locations. Everything else (full charts row,
  // income locations, balance) is admin-only.
  const isAdmin = user?.role === UserRole.admin;

  const from =
    searchParams.get('from') || DateTime.now().startOf('month').toISODate();
  const to =
    searchParams.get('to') || DateTime.now().startOf('day').toISODate();
  const compareToFrom =
    searchParams.get('compareToFrom') ||
    DateTime.now().startOf('month').toISODate();
  const compareToTo =
    searchParams.get('compareToTo') ||
    DateTime.now().startOf('day').toISODate();
  // Support has no compare control, so a compare param carried over from
  // another tab must not apply here either.
  const periodTypeComparedTo = isAdmin
    ? searchParams.get('periodTypeComparedTo') || ''
    : '';

  const isComparing =
    periodTypeComparedTo === ComparedPeriodType.LAST_MONTH ||
    periodTypeComparedTo === ComparedPeriodType.LAST_YEAR ||
    periodTypeComparedTo === ComparedPeriodType.CUSTOM;

  const rawGroupByType = getGroupByType(from, to);
  // /compare does not allow `year`; ChartsFilter custom comparison forces DAY
  // (matches OrdersSection).
  const groupByType =
    periodTypeComparedTo === ComparedPeriodType.CUSTOM
      ? GroupByType.DAY
      : isComparing && rawGroupByType === GroupByType.YEAR
      ? GroupByType.MONTH
      : rawGroupByType;

  const [overview, setOverview] = useState<FinancesOverviewEntity | null>(null);
  const [comparedOverview, setComparedOverview] =
    useState<FinancesComparedOverviewEntity | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  // Bumped after a custom-expense create/delete to re-run the fetch effect.
  const [refreshKey, setRefreshKey] = useState(0);

  async function fetchOverview() {
    const res = await getFinancesOverview({
      from: DateTime.fromISO(from).startOf('day').toISODate() || '',
      to: DateTime.fromISO(to).endOf('day').toISODate() || '',
      timeZone: getTimeZone,
      groupByType,
    });
    setOverview(res.data.data);
  }

  async function fetchComparedOverview(periodType: ComparedPeriodType) {
    const res = await getFinancesComparedOverview({
      from: DateTime.fromISO(from).startOf('day').toISODate() || '',
      to: DateTime.fromISO(to).endOf('day').toISODate() || '',
      compareToType: periodType,
      compareToFrom:
        periodType === ComparedPeriodType.CUSTOM
          ? DateTime.fromISO(compareToFrom).startOf('day').toISODate() || ''
          : undefined,
      compareToTo:
        periodType === ComparedPeriodType.CUSTOM
          ? DateTime.fromISO(compareToTo).endOf('day').toISODate() || ''
          : undefined,
      timeZone: getTimeZone,
      groupByType,
    });
    setComparedOverview(res.data.data);
  }

  useEffect(() => {
    // Always fetch the non-compared overview — the Services Breakdown pie
    // reads from it (the /compare endpoint does not return that field).
    // When comparing, fire the compare call in parallel for the rest.
    let cancelled = false;
    setLoadingOverview(true);

    const run = async () => {
      try {
        if (isComparing) {
          await Promise.all([
            fetchOverview(),
            fetchComparedOverview(periodTypeComparedTo as ComparedPeriodType),
          ]);
        } else {
          await fetchOverview();
          if (!cancelled) setComparedOverview(null);
        }
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.data.message !== 'Unauthorized'
        ) {
          reportError(error);
        }
      } finally {
        if (!cancelled) setLoadingOverview(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, compareToFrom, compareToTo, periodTypeComparedTo, refreshKey]);

  return (
    <Div $mt="32" $mtSM="20">
      <Div>
        <ChartsFilter
          from={from}
          to={to}
          setSearchParams={setSearchParams}
          compareToFrom={compareToFrom || ''}
          compareToTo={compareToTo || ''}
          comparedTo={periodTypeComparedTo}
          hideCompare={!isAdmin}
        />
        <FinancesCharts
          data={overview}
          comparedData={comparedOverview}
          loading={loadingOverview}
          groupByType={groupByType}
          periodTypeComparedTo={
            periodTypeComparedTo as ComparedPeriodType | undefined
          }
          from={from}
          to={to}
          isAdmin={isAdmin}
          onDataChange={() => setRefreshKey((k) => k + 1)}
        />
      </Div>

      <DragScrollX width={!isAdmin ? 'calc(50% - 12px)' : undefined}>
        {isAdmin && <IncomeLocationsList from={from} to={to} />}
        <BrandPayoutList
          from={from}
          to={to}
          sort="desc"
          title="Most Profitable Brands"
          titleTooltip="Based on the provider payout reports (Uber, Grubhub)"
        />
        <BrandPayoutList
          from={from}
          to={to}
          sort="asc"
          title="Least Profitable Brands"
          titleTooltip="Based on the provider payout reports (Uber, Grubhub)"
        />
        {/* Always the last 30 days: it takes no range. */}
        <SaasEligibleLocations />
      </DragScrollX>

      {/* What locations owe vGrubs: admin and support, full width. */}
      <Div $mb="32" $mbSM="20">
        <ClientOutstandingBalance
          from={from}
          to={to}
          groupByType={groupByType}
          periodLabel={renderDateLabelWithToday(from, to)}
        />
      </Div>

      {/* The MyChips games on the tracker: admin only, apart from the income. */}
      {isAdmin && (
        <GamesFinances
          from={from}
          to={to}
          periodLabel={renderDateLabelWithToday(from, to)}
        />
      )}

      {isAdmin && <BalanceFinances />}
    </Div>
  );
};
